import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types"; // For prop-types validation
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import {
  TableContainer,
  Tooltip,
  Paper,
  Box,
  Dialog,
  DialogContent,
  CircularProgress,
  Typography,
  Autocomplete,
  TextField,
  Checkbox,
  IconButton,
  DialogActions,
  Grid,
} from "@mui/material";
import {
  StyledButton,
  StyledCancelButton,
  StyledAlert,
  StyledSnackbar,
  StyledAgGridContainer,
  inputStyle,
  StyledTypographyA,
  DeleteButton,
} from "components/StyledComponents/styledComponents.js";
import { useNavigate } from "react-router-dom";
import useBusinessMonthYear from "components/GlobalHooks/useMonthlyBusiness";
import { writeFile, utils } from "xlsx"; // Import XLSX for Excel export
import DownloadForOfflineIcon from "@mui/icons-material/DownloadForOffline"; // Download icon for the button
import { monthEndStyles } from "components/Styles/monthEndStyles";
import { CustomPaper } from "components/Styles/globalStyles";
import { CheckCircle, Visibility, CloudUpload, Download } from "@mui/icons-material";
import { subDialogContentStyle, dialogContentTextStyle } from "components/Styles/approvalStyles";
import DownloadIcon from "@mui/icons-material/Download";
import URLs from "constants/urls";
import Cookie from "js-cookie";
import MDBox from "components/MDBox";
import dayjs from "dayjs";

export default function MISReview() {
  const { monthIdData, setAlert, handleClose } = useBusinessMonthYear();
  const [monthId, setMonthId] = useState(0);
  const businessCodeRef = useRef(0);
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [alertType, setAlertType] = useState("success");
  const [loading, setLoading] = useState(false);
  const [servicesData, setServicesData] = useState([]);
  const MInput = inputStyle();
  const currentMonthStart = dayjs().startOf("month").format("YYYY-MM-DD");
  const currentMonthEnd = dayjs().endOf("month").format("YYYY-MM-DD");
  const [startDate, setStartDate] = useState(currentMonthStart);
  const [endDate, setEndDate] = useState(currentMonthEnd);
  const [rowData, setRowData] = useState([]);
  const [selectedEntryIds, setSelectedEntryIds] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [footerData, setFooterData] = useState([]);
  const jwt_token = Cookie.get("jwt_token");
  const [paramsData, setParamsData] = useState({});
  const [misDialogOpen, setMisDialogOpen] = useState(false);
  const [misDialogAction, setMisDialogAction] = useState(null);
  const [misDialogMessage, setMisDialogMessage] = useState("");
  const navigate = useNavigate();

  const handleOpenMisDialog = (action, message) => {
    if (selectedEntryIds.length === 0) {
      showAlert("Please select applications", "error");
      return;
    }
    setMisDialogAction(() => action);
    setMisDialogMessage(message);
    setMisDialogOpen(true);
  };

  // Close the MIS dialog
  const handleCloseMisDialog = () => {
    setMisDialogOpen(false);
    setMisDialogAction(null);
    setMisDialogMessage("");
  };

  // Confirm action in the MIS dialog
  const handleConfirmMisDialog = () => {
    if (misDialogAction) {
      misDialogAction();
    }
    handleCloseMisDialog();
  };

  const fetchEntriesByMonthId = async () => {
    if (startDate && endDate) {
      const formattedStartDate = dayjs(startDate).format("YYYY-MM-DD");
      const formattedEndDate = dayjs(endDate).format("YYYY-MM-DD");
      setLoading(true);
      try {
        const options = {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt_token}`,
          },
        };
        const response = await fetch(
          `${URLs.headOfficeMISUrl}?startDate=${formattedStartDate}&endDate=${formattedEndDate}&misStatus=c`,
          options
        );
        const data = await response.json();
        if (response.ok) {
          setServicesData(data);
        } else {
          throw new Error("Failed to fetch entries data");
        }
      } catch (error) {
        showAlert(`Failed to fetch data: ${error.message}`, "error");
      } finally {
        setLoading(false);
      }
    } else {
      showAlert(`Please select both start and end dates`, "error");
    }
  };

  const showAlert = (msg, type) => {
    setMessage(msg);
    setAlertType(type);
    setOpen(true);
  };

  const handleCloseAlert = () => {
    setOpen(false);
  };

  useEffect(() => {
    if (monthIdData && monthIdData.length > 0) {
      // Set monthId only when monthIdData exists and is not empty
      setMonthId(monthIdData[0].businessMonthID);
      fetchEntriesByMonthId();
    }
  }, [monthIdData]);

  useEffect(() => {
    setRowData(servicesData); // Update rowData
    const calculateFooterData = () => {
      const totalLoanAmount = servicesData.reduce((sum, row) => sum + (row.loanAmount || 0), 0);
      const totalRevenue = servicesData.reduce((sum, row) => sum + (row.revenue || 0), 0);
      const totalDSAPayout = servicesData.reduce((sum, row) => sum + (row.dsaPayoutValue || 0), 0);
      const totalRetention = servicesData.reduce(
        (sum, row) => sum + (row.companyRetention || 0),
        0
      );

      setFooterData([
        {
          remarks: "Total",
          loanAmount: totalLoanAmount,
          revenue: totalRevenue,
          dsaPayoutValue: totalDSAPayout,
          companyRetention: totalRetention,
          footerRow: true,
          revenuePercentag: null,
          dsaPayoutPercentage: null,
          retentionPercentage: null,
        },
      ]);
    };
    calculateFooterData(); // Calculate footer data directly with servicesData
  }, [servicesData]);

  const ActionCellRenderer = (props) => {
    const handleDownload = () => {
      if (!servicesData.length || !columns.length) return;
      const filteredColumns = columns.filter((col) => col.headerName !== "Select All");
      const headers = filteredColumns.map((col) => col.headerName);

      const rowData = servicesData.map((row) => {
        const rowObject = {};
        columns.forEach((col) => {
          rowObject[col.headerName] = row[col.field] || "";
        });
        return rowObject;
      });

      const ws = utils.json_to_sheet(rowData, { header: headers });
      const wb = utils.book_new();
      utils.book_append_sheet(wb, ws, "EmployeeData");
      writeFile(wb, "MIS.xlsx");
    };

    return (
      <StyledButton onClick={handleDownload}>
        <DownloadForOfflineIcon fontSize="medium" />
      </StyledButton>
    );
  };

  function formatTimestampToDate(timestamp) {
    const date = new Date(timestamp);
    return date.toISOString().slice(0, 10);
  }

  const handleCheckboxClick = (entryId) => {
    setSelectedEntryIds((prev) => {
      const updatedIds = prev.includes(entryId)
        ? prev.filter((id) => id !== entryId)
        : [...prev, entryId];

      // Update selectedRows based on selectedEntryIds
      const updatedRows = servicesData.filter((row) => updatedIds.includes(row.entryId));
      setSelectedRows(updatedRows);

      return updatedIds;
    });
  };

  const handleSelectAll = () => {
    if (selectedEntryIds.length === servicesData.length) {
      setSelectedEntryIds([]); // Deselect all
      setSelectedRows([]);
    } else {
      setSelectedEntryIds(servicesData.map((row) => row.entryId)); // Select all
      setSelectedRows(servicesData); // Set all rows as selected
    }
  };

  const HeaderCheckbox = () => (
    <>
      <Checkbox
        checked={selectedEntryIds.length === servicesData.length && servicesData.length > 0}
        indeterminate={selectedEntryIds.length > 0 && selectedEntryIds.length < servicesData.length}
        onChange={handleSelectAll}
      />
      <DeleteButton sx={{ fontWeight: "bold", borderRadius: "10px" }}>Select All</DeleteButton>
    </>
  );
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return `${date.getDate()}-${date.toLocaleString("en-US", {
      month: "short",
    })}-${date.getFullYear()}`;
  };
  const triggerS3File = async (params) => {
    setParamsData();
    const url = `${URLs.fecthEmlFile}/${params.data.applicationNumber}`;
    try {
      const downloadResponse = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt_token}`,
        },
      });
      if (downloadResponse.ok) {
        const presignedUrl = await downloadResponse.text();

        if (!presignedUrl.startsWith("http")) {
          throw new Error("Invalid Pre-Signed URL received");
        }
        if (presignedUrl) {
          window.open(presignedUrl, "_blank");
          return;
        }
      }
    } catch {
      showAlert("Failed to Download", "error");
    }
  };
  const columns = [
    {
      field: "addToMIS",
      headerName: "Select All",
      headerComponent: HeaderCheckbox,
      align: "center",
      width: 150,
      cellRenderer: (params) => {
        if (params.node.rowPinned === "bottom") {
          return null;
        }

        return (
          <>
            <Checkbox
              checked={selectedEntryIds.includes(params.data.entryId)}
              onChange={() => handleCheckboxClick(params.data.entryId)}
            />
            <Tooltip title="Download File" arrow>
              <IconButton onClick={() => triggerS3File(params)} color="primary">
                <DownloadIcon />
              </IconButton>
            </Tooltip>
          </>
        );
      },
    },
    {
      field: "date",
      headerName: "Date",
      sortable: true,
      filter: true,
      width: 130,
      valueFormatter: (params) => formatDate(params.value),
      sort: "desc",
    },
    {
      field: "applicationNumber",
      headerName: "Appl. No.",
      sortable: true,
      filter: true,
      flex: 1,
    },
    { field: "customerName", headerName: "Customer Name", sortable: true, filter: true, flex: 1.5 },
    { field: "loanType", headerName: "Business Type", sortable: true, filter: true, flex: 1 },
    { field: "dsaName", headerName: "DSA Name", sortable: true, filter: true },
    { field: "bankName", headerName: "Provider Name", sortable: true, filter: true },
    {
      field: "loanAmount",
      headerName: "Applied Amount",
      sortable: true,
      filter: true,
      flex: 1.5,
      cellStyle: { textAlign: "right", fontWeight: "500" },
      valueFormatter: (params) =>
        `₹ ${params.value?.toLocaleString("en-IN", { minimumFractionDigits: 2 }) || "0.00"}`,
    },
    {
      field: "revenuePercentage",
      headerName: "Est Net %",
      sortable: true,
      filter: true,
      flex: 1.5,
      cellStyle: { textAlign: "right", fontWeight: "500" },
      valueFormatter: (params) => (params.value ? `${params.value.toFixed(2)}%` : ""),
    },
    {
      field: "revenue",
      headerName: "Actual Amount",
      sortable: true,
      filter: true,
      flex: 1.5,
      cellStyle: { textAlign: "right", fontWeight: "500" },
      valueFormatter: (params) =>
        `₹ ${params.value?.toLocaleString("en-IN", { minimumFractionDigits: 2 }) || "0.00"}`,
    },
    {
      field: "dsaPayoutPercentage",
      headerName: "DSA Pay-out (%)",
      sortable: true,
      filter: true,
      flex: 1.5,
      cellStyle: { textAlign: "right", fontWeight: "500" },
      valueFormatter: (params) => (params.value ? `${params.value.toFixed(2)}%` : ""),
    },
    {
      field: "dsaPayoutValue",
      headerName: "DSA Amount",
      sortable: true,
      filter: true,
      flex: 1.5,
      cellStyle: { textAlign: "right", fontWeight: "500" },
      valueFormatter: (params) =>
        `₹ ${params.value?.toLocaleString("en-IN", { minimumFractionDigits: 2 }) || "0.00"}`,
    },
  ];
  const triggerApiCall = () => {
    businessCodeRef.current = monthId;
    fetchEntriesByMonthId();
  };
  const approveMISData = async () => {
    if (selectedEntryIds.length == 0) {
      showAlert("Select Entries", "error");
      return;
    }
    const url = URLs.headOfficeDsaMISApprovalUrl;
    const options = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${jwt_token}`,
      },
      body: JSON.stringify(selectedEntryIds),
    };
    setLoading(true);
    try {
      const response = await fetch(url, options);
      const data = await response.text();
      fetchEntriesByMonthId();
      showAlert(data);
    } catch (error) {
      showAlert(error.message || "Failed to Approve MIS", "error");
    }
    setLoading(false);
  };

  return (
    <DashboardLayout>
      <Dialog
        open={loading}
        onClose={() => setLoading(false)}
        aria-labelledby="loading-dialog-title"
      >
        <DialogContent style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          <CircularProgress color="primary" />
          <Typography variant="h6" style={{ marginTop: "20px" }}>
            Loading, please wait...
          </Typography>
        </DialogContent>
      </Dialog>
      <StyledSnackbar
        open={open}
        autoHideDuration={5000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        severity={alertType}
      >
        <StyledAlert onClose={handleCloseAlert} severity={alertType}>
          {message}
        </StyledAlert>
      </StyledSnackbar>
      <TableContainer component={Paper} sx={monthEndStyles.tableContainer}>
        <Box sx={monthEndStyles.formContainer}>
          <Box sx={monthEndStyles.formItem}>
            <TextField
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              sx={{ ...MInput.b, maxWidth: "140px" }}
              InputLabelProps={{
                shrink: true,
                sx: {
                  color: MInput.a,
                },
              }}
            />
            <TextField
              label="End Date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              sx={{ ...MInput.b, maxWidth: "140px" }}
              InputLabelProps={{
                shrink: true,
                sx: {
                  color: MInput.a,
                },
              }}
            />
            <StyledButton onClick={triggerApiCall}>Get</StyledButton>
          </Box>
          {ActionCellRenderer()}
        </Box>
        <StyledAgGridContainer component={Paper} className={`ag-theme-alpine`}>
          <AgGridReact
            rowData={servicesData}
            columnDefs={columns}
            pagination={true}
            paginationPageSize={10}
            pinnedBottomRowData={footerData}
            animateRows={true}
          />
        </StyledAgGridContainer>
        <MDBox pt={2} px={1} pr={0}>
          <Grid container justifyContent="flex-end">
            <StyledButton
              type="button"
              onClick={() =>
                handleOpenMisDialog(approveMISData, "Are you sure you want to approve?")
              }
            >
              Approve
            </StyledButton>
          </Grid>
        </MDBox>
        <Dialog open={misDialogOpen} onClose={handleCloseMisDialog}>
          <DialogContent sx={{ mt: 1, ...subDialogContentStyle }}>
            {misDialogMessage || "Are you sure you want to proceed with this action?"}
          </DialogContent>
          <DialogActions>
            <StyledCancelButton onClick={handleCloseMisDialog} color="secondary">
              Cancel
            </StyledCancelButton>
            <StyledButton onClick={handleConfirmMisDialog} color="primary">
              Confirm
            </StyledButton>
          </DialogActions>
        </Dialog>
      </TableContainer>
    </DashboardLayout>
  );
}
